import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// Standard MUI upload pattern (Button as label + visually hidden input)
import { SignaturePad } from '../../components/SignaturePad/SignaturePad';
import { projectsAPI, regieReportsAPI } from '../../services/api';
import { useSnackbar } from 'notistack';
import { Project, DailyReport } from '../../types/api';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config';
import CloseIcon from '@mui/icons-material/Close';
import PhotoIcon from '@mui/icons-material/Photo';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';

interface MitarbeiterEintrag {
  name: string;
  typ: string;
  stunden: number | string;
  datum: string;
  material: string;
  maschinen: string;
}

interface FormData {
  project_id: number | string;
  date: Date;
  weather: string;
  activities: string;
  auftraggeber_name: string;
  auftraggeber_adresse: string;
  bauvorhaben: string;
  rapport_nr: string;
  beauftragte_personen: string[];
  beauftragungsart: string;
  beauftragungsbeschreibung: string;
  ort: string;
  unterschrift: string;
  mitarbeiter: MitarbeiterEintrag[];
  ausgefuehrte_arbeiten: string;
  status: string;
  plz_ort: string;
  beauftragung: string;
  auftrag_optionen: string[];
  ort_datum: string;
  unterschrift_auftraggeber: string;
  unterschrift_auftragnehmer: string;
}

export const DailyReportForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<{ label: string; value: number; project: Project }[]>([]);
  const [photos, setPhotos] = useState<File[]>([]);
  const [existingPhotos, setExistingPhotos] = useState<{ id: number; file_path: string }[]>([]);
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);

  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  const [formValues, setFormValues] = useState<FormData>({
    project_id: '',
    date: new Date(),
    weather: '',
    activities: '',
    auftraggeber_name: '',
    auftraggeber_adresse: '',
    bauvorhaben: '',
    rapport_nr: '',
    beauftragte_personen: [],
    beauftragungsart: 'Stundenlohn',
    beauftragungsbeschreibung: '',
    ort: '',
    unterschrift: '',
    mitarbeiter: [],
    ausgefuehrte_arbeiten: '',
    status: 'entwurf',
    plz_ort: '',
    beauftragung: '',
    auftrag_optionen: ['Stundenlohn'],
    ort_datum: '',
    unterschrift_auftraggeber: '',
    unterschrift_auftragnehmer: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Projekte laden
        const projectsResponse = await projectsAPI.getProjects();
        const projectsOptions = projectsResponse.map((project: Project) => ({
          label: project.name,
          value: project.id,
          project: project
        }));
        setProjects(projectsOptions);
        
        // Wenn neuer Bericht, generiere automatisch die nächste Rapport-Nummer
        if (!id) {
          try {
            const nextNumberResponse = await regieReportsAPI.getNextNumber();
            setFormValues(prev => ({
              ...prev,
              rapport_nr: nextNumberResponse.next_number
            }));
          } catch (error) {
            console.error('Error fetching next rapport number:', error);
            // Fallback: Generiere eine Nummer basierend auf Timestamp
            const fallbackNumber = Date.now().toString().slice(-6);
            setFormValues(prev => ({
              ...prev,
              rapport_nr: fallbackNumber
            }));
          }
        }
        
        // Wenn im Bearbeitungsmodus, lade den bestehenden Bericht
        if (id) {
          const report = await regieReportsAPI.getReport(Number(id));
          setFormValues({
            project_id: report.project_id || '',
            date: report.date ? new Date(report.date) : new Date(),
            weather: report.weather || '',
            activities: report.activities || '',
            auftraggeber_name: report.auftraggeber_name || '',
            auftraggeber_adresse: report.auftraggeber_adresse || '',
            bauvorhaben: report.bauvorhaben || '',
            rapport_nr: report.rapport_nr || '',
            beauftragte_personen: report.beauftragte_personen || [],
            beauftragungsart: report.beauftragungsart || 'Stundenlohn',
            beauftragungsbeschreibung: report.beauftragungsbeschreibung || '',
            ort: report.ort || '',
            unterschrift: report.unterschrift || '',
            mitarbeiter: report.mitarbeiter || [],
            ausgefuehrte_arbeiten: report.ausgefuehrte_arbeiten || '',
            status: report.status || 'entwurf',
            plz_ort: report.plz_ort || '',
            beauftragung: report.beauftragung || '',
            auftrag_optionen: report.auftrag_optionen || ['Stundenlohn'],
            ort_datum: report.ort_datum || '',
            unterschrift_auftraggeber: report.unterschrift_auftraggeber || '',
            unterschrift_auftragnehmer: report.unterschrift_auftragnehmer || '',
          });
          setExistingPhotos(report.photos || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        enqueueSnackbar('Fehler beim Laden der Daten', { variant: 'error' });
      }
    };

    fetchData();
  }, [id, enqueueSnackbar]);

  // Automatische Projekt-Stammdaten-Übernahme
  const handleProjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedId = e.target.value ? Number(e.target.value) : null;
    
    if (selectedId) {
      const selectedProject = projects.find((p) => p.project.id === selectedId);
      if (selectedProject) {
        const project = selectedProject.project;
        
        // Automatisches Befüllen der Stammdaten aus dem Projekt
        setFormValues(prev => ({
          ...prev,
          project_id: selectedId,
          auftraggeber_name: project.client_name || '',
          auftraggeber_adresse: project.site_address || '',
          bauvorhaben: project.description || project.name || '',
          ort: extractCityFromAddress(project.site_address || ''),
          plz_ort: extractPLZAndCityFromAddress(project.site_address || ''),
          ort_datum: `${extractCityFromAddress(project.site_address || '')}, ${new Date().toLocaleDateString('de-DE')}`,
        }));
        
        enqueueSnackbar('Projekt-Stammdaten wurden automatisch übernommen', { variant: 'success' });
      }
    } else {
      // Wenn kein Projekt ausgewählt, Felder zurücksetzen
      setFormValues(prev => ({
        ...prev,
        project_id: '',
        auftraggeber_name: '',
        auftraggeber_adresse: '',
        bauvorhaben: '',
        ort: '',
        plz_ort: '',
      }));
    }
  };

  // Hilfsfunktionen um PLZ und Stadt aus Adresse zu extrahieren
  const extractCityFromAddress = (address: string): string => {
    if (!address) return '';
    
    // Versuche verschiedene deutsche Adressformate zu parsen
    const lines = address.split('\n');
    const lastLine = lines[lines.length - 1].trim();
    
    // Format: "PLZ Stadt" oder "PLZ Ort"
    const plzCityMatch = lastLine.match(/^\d{5}\s+(.+)$/);
    if (plzCityMatch) {
      return plzCityMatch[1]; // Gibt nur die Stadt zurück (ohne PLZ)
    }
    
    // Fallback: verwende die letzte Zeile
    return lastLine;
  };

  const extractPLZFromAddress = (address: string): string => {
    if (!address) return '';
    
    // Versuche verschiedene deutsche Adressformate zu parsen
    const lines = address.split('\n');
    const lastLine = lines[lines.length - 1].trim();
    
    // Format: "PLZ Stadt" oder "PLZ Ort"
    const plzMatch = lastLine.match(/^(\d{5})\s+(.+)$/);
    if (plzMatch) {
      return plzMatch[1]; // Gibt nur die PLZ zurück
    }
    
    return '';
  };

  const extractPLZAndCityFromAddress = (address: string): string => {
    if (!address) return '';
    
    // Versuche verschiedene deutsche Adressformate zu parsen
    const lines = address.split('\n');
    const lastLine = lines[lines.length - 1].trim();
    
    // Format: "PLZ Stadt" oder "PLZ Ort"
    const plzCityMatch = lastLine.match(/^\d{5}\s+(.+)$/);
    if (plzCityMatch) {
      return lastLine; // Gibt "PLZ Stadt" zurück
    }
    
    // Fallback: verwende die letzte Zeile
    return lastLine;
  };

  const handleInputChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setFormValues(prev => ({
        ...prev,
        date
      }));
    }
  };

  const addMitarbeiter = () => {
    const newMitarbeiter: MitarbeiterEintrag = {
      name: '',
      typ: '',
      stunden: '',
      datum: formValues.date.toISOString().split('T')[0],
      material: '',
      maschinen: ''
    };
    
    setFormValues(prev => ({
      ...prev,
      mitarbeiter: [...prev.mitarbeiter, newMitarbeiter]
    }));
  };

  const removeMitarbeiter = (index: number) => {
    setFormValues(prev => ({
      ...prev,
      mitarbeiter: prev.mitarbeiter.filter((_, i) => i !== index)
    }));
  };

  const updateMitarbeiter = (index: number, field: keyof MitarbeiterEintrag, value: string | number) => {
    setFormValues(prev => ({
      ...prev,
      mitarbeiter: prev.mitarbeiter.map((m, i) => 
        i === index ? { ...m, [field]: value } : m
      )
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validierung
    if (!formValues.project_id) {
      enqueueSnackbar('Bitte wählen Sie ein Projekt aus', { variant: 'error' });
      return;
    }
    
    if (!formValues.weather) {
      enqueueSnackbar('Bitte geben Sie das Wetter an', { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      
      // Convert form values to the expected format
      const reportData = {
        ...formValues,
        date: formValues.date.toISOString().split('T')[0], // Convert to YYYY-MM-DD format
        project_id: Number(formValues.project_id),
      };
      
      formData.append('report', JSON.stringify(reportData));

      // Append photos if any
      if (photos.length > 0) {
        photos.forEach(photo => {
          formData.append('photos', photo);
        });
      }

      if (id) {
        await regieReportsAPI.updateReport(Number(id), formData);
        enqueueSnackbar('Regiebericht erfolgreich aktualisiert', { variant: 'success' });
      } else {
        await regieReportsAPI.createReport(formData);
        enqueueSnackbar('Regiebericht erfolgreich erstellt', { variant: 'success' });
      }
      
      navigate('/regie-reports');
    } catch (error) {
      console.error('Error saving report:', error);
      enqueueSnackbar('Fehler beim Speichern des Regieberichts', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/regie-reports');
  };

  const getPhotoUrl = (path: string): string => {
    if (!path) return '';
    
    // Remove leading 'static/' if present since it's included in the API path
    const cleanPath = path.replace(/^static\//, '');
    
    // Construct the full URL - note we need to go up one level since photos are served from /static
    return `${API_BASE_URL.replace(/\/api$/, '')}/static/${cleanPath}`;
  };

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        {id ? 'Regiebericht bearbeiten' : 'Neuer Regiebericht'}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Grunddaten
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Projekt *"
                  value={formValues.project_id}
                  onChange={handleProjectChange}
                  required
                >
                  <MenuItem value="">
                    <em>Projekt auswählen</em>
                  </MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.value} value={project.value}>
                      {project.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Datum *"
                  value={formValues.date}
                  onChange={handleDateChange}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Auftraggeber & Projekt
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Auftraggeber Name"
                  value={formValues.auftraggeber_name}
                  onChange={handleInputChange('auftraggeber_name')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bauvorhaben"
                  value={formValues.bauvorhaben}
                  onChange={handleInputChange('bauvorhaben')}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Auftraggeber Adresse"
                  value={formValues.auftraggeber_adresse}
                  onChange={handleInputChange('auftraggeber_adresse')}
                  helperText="Straße, PLZ und Ort in einem Feld"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Rapport Nr."
                  value={formValues.rapport_nr}
                  onChange={handleInputChange('rapport_nr')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Wetter & Bedingungen
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Wetter *"
                  value={formValues.weather}
                  onChange={handleInputChange('weather')}
                  required
                >
                  <MenuItem value="sunny">Sonnig</MenuItem>
                  <MenuItem value="cloudy">Bewölkt</MenuItem>
                  <MenuItem value="rainy">Regnerisch</MenuItem>
                  <MenuItem value="snowy">Schnee</MenuItem>
                  <MenuItem value="windy">Windig</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Mitarbeiter
            </Typography>
            <Button variant="outlined" onClick={addMitarbeiter} sx={{ mb: 2 }}>
              Mitarbeiter hinzufügen
            </Button>
            
            {formValues.mitarbeiter.length > 0 && (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Typ</TableCell>
                      <TableCell>Stunden</TableCell>
                      <TableCell>Material</TableCell>
                      <TableCell>Maschinen</TableCell>
                      <TableCell>Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formValues.mitarbeiter.map((mitarbeiter, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            size="small"
                            value={mitarbeiter.name}
                            onChange={(e) => updateMitarbeiter(index, 'name', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={mitarbeiter.typ}
                            onChange={(e) => updateMitarbeiter(index, 'typ', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={mitarbeiter.stunden || ''}
                            onChange={(e) => updateMitarbeiter(index, 'stunden', e.target.value === '' ? '' : Number(e.target.value))}
                            placeholder="Stunden eingeben"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={mitarbeiter.material}
                            onChange={(e) => updateMitarbeiter(index, 'material', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={mitarbeiter.maschinen}
                            onChange={(e) => updateMitarbeiter(index, 'maschinen', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            color="error"
                            onClick={() => removeMitarbeiter(index)}
                          >
                            Entfernen
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Ausgeführte Arbeiten
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Beschreibung der ausgeführten Arbeiten"
              value={formValues.ausgefuehrte_arbeiten}
              onChange={handleInputChange('ausgefuehrte_arbeiten')}
            />
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Fotos
            </Typography>
            {/* iOS-kompatibler Foto-Upload ohne accept-Attribute */}
            <Button
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              sx={{ mb: 1 }}
              onClick={() => {
                // Erstelle einen versteckten File-Input für iOS-Kompatibilität
                const input = document.createElement('input');
                input.type = 'file';
                input.multiple = true;
                // KEINE accept-Attribute - das verursacht iOS-Probleme
                input.style.display = 'none';
                input.onchange = (e: Event) => {
                  const target = e.target as HTMLInputElement;
                  const fileList = target.files ? Array.from(target.files) : [];
                  setPhotos(fileList);
                };
                document.body.appendChild(input);
                input.click();
                document.body.removeChild(input);
              }}
            >
              Fotos auswählen
            </Button>
            <Typography variant="body2" color="textSecondary">
              Fotos hier ablegen oder klicken zum Auswählen (max. 5 Fotos)
            </Typography>

            {/* Anzeige der neu ausgewählten Fotos */}
            {photos.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Neu ausgewählte Fotos ({photos.length}):
                </Typography>
                <Grid container spacing={2}>
                  {photos.map((photo, index) => (
                    <Grid item key={index}>
                      <Box
                        sx={{ 
                          position: 'relative',
                          width: 100,
                          height: 100,
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer'
                        }}
                      >
                        <img
                          src={URL.createObjectURL(photo)}
                          alt={`Neues Foto ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.9)'
                            }
                          }}
                          onClick={() => {
                            const newPhotos = photos.filter((_, i) => i !== index);
                            setPhotos(newPhotos);
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
            
            {existingPhotos.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Vorhandene Fotos:
                </Typography>
                <Grid container spacing={2}>
                  {existingPhotos.map((photo, index) => (
                    <Grid item key={photo.id}>
                      <Box
                        sx={{ 
                          position: 'relative',
                          width: 100,
                          height: 100,
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer'
                        }}
                        onClick={() => setPreviewPhoto(getPhotoUrl(photo.file_path))}
                      >
                        <img
                          src={getPhotoUrl(photo.file_path)}
                          alt={`Foto ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            '&:hover': { opacity: 1 }
                          }}
                        >
                          <PhotoIcon sx={{ color: 'white' }} />
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Unterschriften
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Unterschrift Auftraggeber
                </Typography>
                <SignaturePad
                  value={formValues.unterschrift_auftraggeber}
                  onChange={(signature) => setFormValues(prev => ({
                    ...prev,
                    unterschrift_auftraggeber: signature
                  }))}
                  width={350}
                  height={180}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Unterschrift Auftragnehmer
                </Typography>
                <SignaturePad
                  value={formValues.unterschrift_auftragnehmer}
                  onChange={(signature) => setFormValues(prev => ({
                    ...prev,
                    unterschrift_auftragnehmer: signature
                  }))}
                  width={350}
                  height={180}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ort, Datum"
                  value={formValues.ort_datum}
                  onChange={handleInputChange('ort_datum')}
                  placeholder="z.B. München, 05.08.2025"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Status */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Status
            </Typography>
            <TextField
              select
              name="status"
              label="Status"
              value={formValues.status}
              onChange={handleInputChange('status')}
              fullWidth
              required
            >
              <MenuItem value="entwurf">Entwurf</MenuItem>
              <MenuItem value="eingereicht">Eingereicht</MenuItem>
              <MenuItem value="genehmigt">Genehmigt</MenuItem>
            </TextField>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleCancel}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Speichere...' : (id ? 'Aktualisieren' : 'Erstellen')}
          </Button>
        </Box>
      </form>

      {/* Photo Preview Dialog */}
      <Dialog
        open={!!previewPhoto}
        onClose={() => setPreviewPhoto(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Foto Vorschau
          <IconButton
            onClick={() => setPreviewPhoto(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {previewPhoto && (
            <img
              src={previewPhoto}
              alt="Vorschau"
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DailyReportForm;